import { enabled, limit } from "@giftforward/entitlements";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { useNavigate } from "react-router-dom";
import Accordion from "@/components/campaignBuilder/Accordion";
import ReminderDateTimePicker from "@/components/campaigns/ReminderDateTimePicker";
import { REMINDER_SCHEDULES } from "@/constants/campaignBuilder";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import { useReminders } from "@/hooks/useReminders";
import {
  combineDateTime,
  describeReminderScheduleViolation,
  formatDisplayDate,
  parseTime,
  previewReminderDates,
  reminderDateBounds,
  resolveEffectiveWindow,
  validateReminderSchedule,
} from "@/lib/reminders";
import { isoToZonedSlot, resolveCampaignTimeZone } from "@/utils/timezone-helper";
import { ConditionalPanel, RadioOption, ReminderActionButtons } from "./shared";

interface Props {
  /**
   * Step 2 is locked (campaign past draft), so the section is rendered open — the layer above it
   * has `pointer-events: none`, which also swallows the accordion header click, and a section the
   * sender can neither read nor reach would be hidden rather than locked. Read-only: it opens the
   * accordion, it never enables anything inside it.
   */
  forceOpen?: boolean;
}

type Slot = { date: string; time: string };
const EMPTY_SLOT: Slot = { date: "", time: "" };

/** "Sep 17, 2026 at 9:00 AM" — the absolute label a set date replaces the relative one with. */
function formatAbsoluteLabel(date: string, time: string): string {
  if (!date) return "";
  const { h, m, ap } = parseTime(time);
  return `${formatDisplayDate(date)} at ${Number.parseInt(h, 10)}:${m} ${ap}`;
}

export default function GiftReminders({ forceOpen = false }: Props) {
  const navigate = useNavigate();
  const {
    openModal,
    openPlanModal,
    reminderCount,
    setReminderCount,
    remindersOpen,
    setRemindersOpen,
    campaignId,
    reminderEmails,
    setReminderEmail,
    scheduleMode,
    scheduleDate,
    scheduleTime,
    expiryMode,
    customExpiryDate,
    scheduleTz,
    notificationMode,
    activationTz,
  } = useCampaignBuilder();
  // The campaign's zone (activation pick if manual, schedule pick otherwise) — the one the server reads picks in.
  const timeZone = resolveCampaignTimeZone({ notificationMode, scheduleTz, activationTz });
  const { ent } = useMyEntitlements();
  // Starter's cap is 0, which is a real limit and not an unset one — the accordion is locked
  // because the plan allows no reminders at all, not because it is not a paid plan.
  const maxReminders = ent ? limit(ent, "campaign_reminders", "max") : 0;
  const canSendReminders = maxReminders === null || maxReminders > 0;
  const canCustomizeDates = !!ent && enabled(ent, "campaign_reminders", "custom_dates");

  function openUpgradeModal() {
    openModal({
      context: "giftReminders",
      onPrimary: () => openPlanModal("giftReminders"),
    });
  }

  function handleToggle() {
    if (canSendReminders) {
      setRemindersOpen(!remindersOpen);
    } else {
      openUpgradeModal();
    }
  }

  const schedule = REMINDER_SCHEDULES[reminderCount] ?? [];

  // The auto-calculated default (Priority 1) — read via the same endpoint My Campaigns reads, for
  // display/pre-fill only. Saving a Pro sender's own pick goes through the normal Step 2 autosave
  // (below), not this hook — same mechanism as everything else in this accordion.
  const { data: persisted } = useReminders(campaignId, canSendReminders && reminderCount > 0);
  const persistedSlots: Slot[] = [EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT];
  for (const r of persisted?.upcoming ?? []) {
    const match = r.id.match(/^reminder-(\d)$/);
    // Already split in the campaign's zone by the fetch (api/reminders.ts).
    if (match?.[1]) persistedSlots[Number(match[1]) - 1] = { date: r.date, time: r.time };
  }

  /**
   * What the sender has actually COMMITTED to this slot: their own pick, else what the server has
   * stored, else nothing. Deliberately excludes the preview — see `displaySlot`.
   */
  function chosenSlot(idx: number): Slot {
    const email = reminderEmails[idx];
    if (email?.sendDate) return { date: email.sendDate, time: email.sendTime ?? "09:00" };
    return persistedSlots[idx] ?? EMPTY_SLOT;
  }

  /**
   * What the row RENDERS: the committed value, or the local preview when there is none.
   *
   * Split from `chosenSlot` because the preview is a statement about what a save would write, not
   * a value the sender picked — and everything that constrains or judges them must only ever see
   * what they picked. Feeding it into the calendar bounds boxed each row in between two dates
   * nobody had chosen: on a fresh ASAP campaign, reminder 1 could only land in the five days
   * before reminder 2's *suggested* slot. Feeding it into the validation would have reported the
   * suggestion itself as the sender's error.
   */
  function displaySlot(idx: number): Slot {
    const chosen = chosenSlot(idx);
    if (chosen.date) return chosen;
    // Once ANY slot is committed the preview stops being true of the empty ones, so they show as
    // what they are: empty. See `noneChosen`.
    return noneChosen ? (previewSlots[idx] ?? EMPTY_SLOT) : EMPTY_SLOT;
  }

  /** Marks the slot touched — it rides the normal Step 2 autosave from here on, same as every
   *  other field in this accordion (reminder count, content). Clearing the date reverts to
   *  untouched, so the auto-calculated default takes back over. */
  function updateSlot(idx: number, date: string, time: string) {
    setReminderEmail(idx, { sendDate: date || undefined, sendTime: date ? time : undefined });
  }

  // Checked against the SAME 3 rules the server enforces on save (dates-helper.ts's
  // validateReminderSchedule) — but live, at the input, rather than only after a save round-trip
  // lands as a toast on whatever screen the sender has since navigated to (Step 1/2/4 saves fire
  // on the way out and never block the navigation).
  const { sendDate: effectiveSendDate, expireDate: effectiveExpireDate } = resolveEffectiveWindow({
    scheduleMode,
    scheduleDate,
    scheduleTime,
    expiryMode,
    customExpiryDate,
    timeZone,
  });
  // Only the slots the sender can currently see and edit — a slot beyond `reminderCount` may
  // still hold a touched value from a moment ago (switching from 4 reminders to 3 doesn't clear
  // it), but it's no longer part of what's being saved or shown, so it must not surface a
  // "Reminder 4" error for a row that isn't even on the screen anymore.
  /**
   * What a save would write right now, computed locally — the answer to "which dates am I about
   * to schedule?" while the sender is still on the step. The server is only asked on the way OUT
   * of Step 2, and a campaign that has not been saved once has no id at all, which disables
   * `useReminders` outright; without this the sender configures a schedule and an expiry and
   * watches four empty pickers, unable to tell "too short a window for any reminder" from "not
   * loaded yet". Replaced by the server's own answer as soon as one exists.
   */
  const previewSlots: Slot[] = previewReminderDates(
    effectiveSendDate,
    effectiveExpireDate,
    reminderCount,
    timeZone,
  ).map((iso) => {
    if (!iso) return EMPTY_SLOT;
    return isoToZonedSlot(iso, timeZone);
  });

  const currentDates = [0, 1, 2, 3].map((i) => {
    if (i >= reminderCount) return null;
    const slot = chosenSlot(i);
    return combineDateTime(slot.date, slot.time, timeZone);
  }) as [string | null, string | null, string | null, string | null];

  /**
   * Whether the campaign holds no reminder date at all — the one state in which the preview is a
   * true statement about the future.
   *
   * The launch only derives the schedule for a campaign that has NONE set (`hasReminderDates` in
   * campaignLaunchService), precisely so it can't stomp a sender's own pick. So the moment one
   * slot is committed, the empty ones stay empty through launch and forever after: nothing will
   * ever fill them. Showing a suggestion there promises an email that is never sent, which is a
   * worse lie than a blank field — the sender reads "reminder 4 goes out the day before expiry"
   * off a slot the system has already decided to skip.
   */
  const noneChosen = currentDates.every((iso) => iso === null);
  const violation = canCustomizeDates
    ? validateReminderSchedule(currentDates, effectiveSendDate, effectiveExpireDate)
    : null;

  // Calendar bounds per row. `currentDates` already nulls every slot beyond `reminderCount`, so a
  // row that is no longer on screen cannot bound one that is — see `reminderDateBounds`, shared
  // with My Campaigns' Notifications modal so the two surfaces can't drift apart.
  function boundsFor(idx: number) {
    return reminderDateBounds(currentDates, idx, effectiveSendDate, effectiveExpireDate, timeZone);
  }

  return (
    <Accordion
      title="Gift Reminders"
      description="Gentle nudges to claim gifts before they expire"
      icon={<NotificationsActiveIcon sx={{ fontSize: 24 }} />}
      // Two levels on purpose. The OUTER `canSendReminders` is the PAYWALL, not our status lock:
      // for a sender without the entitlement this accordion is a closed upsell whose header opens
      // the upgrade modal (`handleToggle` → `openUpgradeModal`), so `forceOpen` must never reach
      // it — revealing entitled content to an unentitled account would be a worse bug than a
      // collapsed section. `forceOpen` therefore only ORs with `remindersOpen` INSIDE the
      // entitled branch.
      isOpen={canSendReminders ? forceOpen || remindersOpen : false}
      onToggle={handleToggle}
      locked={!canSendReminders}
      onUpgradeClick={openUpgradeModal}
    >
      <div className="flex flex-col gap-2 mt-4">
        <RadioOption
          selected={reminderCount === 4}
          onSelect={() => setReminderCount(4)}
          label="4 reminders"
          hint="Maximizes redemption with gentle, well-timed nudges."
          recommended
        />
        <RadioOption
          selected={reminderCount === 3}
          onSelect={() => setReminderCount(3)}
          label="3 reminders"
        />
        <RadioOption
          selected={reminderCount === 2}
          onSelect={() => setReminderCount(2)}
          label="2 reminders"
        />
        <RadioOption
          selected={reminderCount === 1}
          onSelect={() => setReminderCount(1)}
          label="1 reminder"
        />
        <RadioOption
          selected={reminderCount === 0}
          onSelect={() => setReminderCount(0)}
          label="No reminders"
          hint="Not recommended — unredeemed gifts mean wasted budget."
        />
      </div>

      {/* Reminder Schedule — wax panel with timeline, visible when count > 0 */}
      <ConditionalPanel visible={reminderCount > 0}>
        <p
          className="font-lato text-[11px] font-bold uppercase tracking-[2px] mb-3"
          style={{ color: "var(--color-dark-dust)", marginTop: 0 }}
        >
          Reminder Schedule
        </p>

        {/* Timeline */}
        <div className="relative flex flex-col pl-5">
          {/* Vertical connecting line */}
          <div
            className="absolute top-2 bottom-2 w-0.5"
            style={{ left: "7px", background: "var(--color-smoke)" }}
          />
          {schedule.map((item, idx) => {
            const slot = displaySlot(idx);
            const absoluteLabel = formatAbsoluteLabel(slot.date, slot.time);
            return (
              <div key={item.label} className="relative flex items-center gap-3.5 py-2.5 flex-wrap">
                {/* Dot */}
                <div
                  className="absolute w-3 h-3 rounded-full border-2 border-white"
                  style={{ left: "-18px", background: "var(--color-rodeo-dust)" }}
                />

                <div className="flex-1 min-w-[140px]">
                  <p className="font-lato text-[13px] font-semibold text-rodeo-black">
                    {item.label}
                  </p>
                  <p className="font-lato text-[12px] text-graphite">
                    {absoluteLabel || item.timing}
                  </p>
                </div>

                {canCustomizeDates && (
                  <div className="w-[220px] shrink-0">
                    <ReminderDateTimePicker
                      value={slot}
                      onChange={(date, time) => updateSlot(idx, date, time)}
                      error={violation?.order === idx + 1}
                      minDate={boundsFor(idx).minDate}
                      maxDate={boundsFor(idx).maxDate}
                    />
                  </div>
                )}

                <ReminderActionButtons
                  onEdit={() =>
                    navigate("/campaign/create/reminders", {
                      state: { reminderIndex: idx, mode: "edit" },
                    })
                  }
                  onPreview={() =>
                    navigate("/campaign/create/reminders", {
                      state: { reminderIndex: idx, mode: "preview" },
                    })
                  }
                />
              </div>
            );
          })}
        </div>

        {violation && (
          <p className="font-lato text-[12px] mt-3" style={{ color: "var(--color-good-wine)" }}>
            {describeReminderScheduleViolation(violation)}
          </p>
        )}
      </ConditionalPanel>
    </Accordion>
  );
}
